// Plan Design Review §6.8-2 - 공개 신청양식 진행률 계산 helper.
// Default / TypeA / TypeB PreRegisterDialog 3종이 공유한다.
//
// PM 결정 기준:
//  - required key: child.requiredYn === 'Y'
//  - 값 판정:
//      TEXT/EMAIL/MOBILE/TEXTAREA/SELECT/HOSPITAL/MANAGER/MANAGER_TEXT/MANAGER_SELECT
//        -> child.value 존재 (trim 후 빈 문자열 제외)
//      CHECKBOX/RADIO -> child.opts 중 checked === true 한 개 이상
//      TERMS / 그리고 isTermsGroup 안의 child -> child.checked === true
//  - SESSION/EVENT 진행기준 -> eventList의 onlineChecked || offlineChecked
//  - useYn !== 'Y' && !isAlwaysShow 인 child는 카운트 제외 (운영자에게 보이지 않으므로 필수 검증 대상 아님)
//  - SECTION inputType 은 자체 카운트 제외, children 재귀 traverse
//
// 반환 shape:
//   {
//     requiredFields: { filled: number, total: number },
//     terms: { agreed: number, total: number },
//     session: { selected: number, required: number },
//     percentComplete: number 0~100,
//   }

const VALUE_INPUT_TYPES = new Set([
  "TEXT",
  "EMAIL",
  "MOBILE",
  "TEXTAREA",
  "SELECT",
  "HOSPITAL",
  "MANAGER",
  "MANAGER_TEXT",
  "MANAGER_SELECT",
]);

const CHOICE_INPUT_TYPES = new Set(["CHECKBOX", "RADIO"]);

const isVisibleChild = (child) =>
  child?.useYn === "Y" || Boolean(child?.isAlwaysShow);

const isFieldFilled = (child) => {
  if (!child) return false;
  const inputType = child.inputType;
  if (VALUE_INPUT_TYPES.has(inputType)) {
    const v = child.value;
    if (v === undefined || v === null) return false;
    return String(v).trim() !== "";
  }
  if (CHOICE_INPUT_TYPES.has(inputType)) {
    if (!Array.isArray(child.opts)) return false;
    return child.opts.some((opt) => opt?.checked === true);
  }
  if (inputType === "TERMS") {
    return child.checked === true;
  }
  return false;
};

const traverseChildren = (parent, buckets) => {
  const children = Array.isArray(parent?.children) ? parent.children : [];
  for (const child of children) {
    if (!child) continue;
    if (child.inputType === "SECTION") {
      traverseChildren(child, buckets);
      continue;
    }
    if (!isVisibleChild(child)) continue;

    // TERMS inputType 또는 부모가 isTermsGroup === 'Y' 인 경우 terms bucket
    const inheritsTermsGroup = parent?.isTermsGroup === "Y";
    const isTerms = child.inputType === "TERMS" || inheritsTermsGroup;

    if (child.requiredYn !== "Y") continue;

    if (isTerms) {
      buckets.terms.total += 1;
      const agreed = inheritsTermsGroup
        ? child.checked === true
        : isFieldFilled(child);
      if (agreed) buckets.terms.agreed += 1;
      continue;
    }

    buckets.requiredFields.total += 1;
    if (isFieldFilled(child)) {
      buckets.requiredFields.filled += 1;
    }
  }
};

const hasEventListSelection = (eventList) => {
  if (!Array.isArray(eventList) || eventList.length === 0) return false;
  return eventList.some(
    (event) => event?.onlineChecked === true || event?.offlineChecked === true
  );
};

export function calculatePreRegisterProgress({
  myPreRegInfoList,
  eventList,
  progressStandard,
} = {}) {
  const buckets = {
    requiredFields: { filled: 0, total: 0 },
    terms: { agreed: 0, total: 0 },
    session: { selected: 0, required: 0 },
  };

  // SESSION 또는 EVENT 진행기준일 때만 session/event 선택을 진행률에 포함.
  if (progressStandard === "SESSION" || progressStandard === "EVENT") {
    buckets.session.required = 1;
    buckets.session.selected = hasEventListSelection(eventList) ? 1 : 0;
  }

  for (const item of (myPreRegInfoList || [])) {
    if (!item) continue;
    traverseChildren(item, buckets);
  }

  const totalRequired =
    buckets.requiredFields.total + buckets.terms.total + buckets.session.required;
  const totalCompleted =
    buckets.requiredFields.filled + buckets.terms.agreed + buckets.session.selected;
  const percentComplete = totalRequired === 0
    ? 100
    : Math.round((totalCompleted / totalRequired) * 100);

  return {
    requiredFields: buckets.requiredFields,
    terms: buckets.terms,
    session: buckets.session,
    percentComplete,
  };
}

export default calculatePreRegisterProgress;
